import React, { FC, useRef } from 'react';
import styles from './styles.module.scss';
import { occupation, TCard } from '@xeppt/xeppt-sdk/types';
import { formatDate } from '@utils/index';
import Button from '@components/common/button';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { IModalComponentProps } from '../../../context/modal_context';
import { useAgreements } from '@hooks/api/useAgreement';

const CardAgreementModal: FC<IModalComponentProps<TCard>> = ({ modalData }) => {
    const printRef = useRef<HTMLDivElement>(null);
    if (!modalData?.id) return null;

    const { agreement_details, agreement_accepted_at, agreement_document_id } = modalData;

    const handlePrint = () => {
        if (printRef.current) {
            const printContents = printRef.current.outerHTML; // Isolate the element contents
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.open();
                printWindow.document.write(`
          <html lang="en">
            <head>
              <title>Cardholder Agreement</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                }
              </style>
            </head>
            <body onload="window.print(); window.close();">
              ${printContents}
            </body>
          </html>
        `);
                printWindow.document.close();
            }
        }
    };

    const handleDownloadPDF = async () => {
        if (printRef.current) {
            const element = printRef.current;

            // Store original styles
            const originalHeight = element.style.height;
            const originalMaxHeight = element.style.maxHeight;
            const originalOverflow = element.style.overflow;

            // Add CSS for better page breaks
            const style = document.createElement('style');
            style.textContent = `
                .pdf-content table { page-break-inside: avoid; break-inside: avoid; }
                .pdf-content tr { page-break-inside: avoid; break-inside: avoid; }
                .pdf-content h1, .pdf-content h2, .pdf-content h3 {
                    page-break-after: avoid;
                    break-after: avoid;
                }
                .pdf-content p {
                    page-break-inside: avoid;
                    break-inside: avoid;
                    margin-bottom: 10px;
                }
                .pdf-content hr {
                    page-break-after: avoid;
                    break-after: avoid;
                    margin: 20px 0;
                }
            `;
            document.head.appendChild(style);

            // Add class for PDF-specific styling
            element.classList.add('pdf-content');

            // Temporarily remove height restrictions to capture full content
            element.style.height = 'auto';
            element.style.maxHeight = 'none';
            element.style.overflow = 'visible';

            // Wait for layout to adjust
            await new Promise((resolve) => setTimeout(resolve, 200));

            try {
                // Get all potential break points (elements that should not be split)
                const breakElements = element.querySelectorAll('table, tr, h1, h2, h3, p, hr');
                const breakPoints = Array.from(breakElements)
                    .map((el) => {
                        const rect = el.getBoundingClientRect();
                        const elementRect = element.getBoundingClientRect();
                        return {
                            offsetTop: rect.top - elementRect.top,
                            offsetBottom: rect.bottom - elementRect.top,
                            height: rect.height,
                            element: el.tagName
                        };
                    })
                    .sort((a, b) => a.offsetTop - b.offsetTop);

                // Render the content into a canvas with optimized settings
                const canvas = await html2canvas(element, {
                    scale: 2, // Higher scale for better quality
                    useCORS: true,
                    height: element.scrollHeight,
                    windowHeight: element.scrollHeight,
                    scrollX: 0,
                    scrollY: 0,
                    backgroundColor: '#ffffff',
                    removeContainer: true,
                    imageTimeout: 0,
                    logging: false
                });

                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = 210;
                const pdfHeight = 297;
                const margin = 15;
                const contentWidth = pdfWidth - 2 * margin;
                const contentHeight = pdfHeight - 2 * margin;

                // Convert measurements
                const pixelsToMm = 0.264583 / 2; // Adjust for scale=2
                const canvasWidthMm = canvas.width * pixelsToMm;
                const canvasHeightMm = canvas.height * pixelsToMm;

                // Scale to fit width
                const scale = contentWidth / canvasWidthMm;
                const scaledHeightMm = canvasHeightMm * scale;

                // Function to find the best break point near a target position
                const findBestBreakPoint = (targetYMm: any, maxYMm: any) => {
                    const targetYPx = targetYMm / scale / pixelsToMm;
                    const maxYPx = maxYMm / scale / pixelsToMm;

                    // Find break points in the target range
                    const candidateBreaks = breakPoints.filter(
                        (bp) => bp.offsetTop >= targetYPx - 50 && bp.offsetTop <= maxYPx
                    );

                    if (candidateBreaks.length === 0) {
                        // No good break point found, use the max position but try to avoid cutting text
                        return Math.max(targetYMm, maxYMm - 20); // Leave some margin
                    }

                    // Find the break point closest to but before the max
                    const bestBreak = candidateBreaks
                        .filter((bp) => bp.offsetTop <= maxYPx - 10) // Leave some margin
                        .pop(); // Get the last (highest) one

                    if (bestBreak) {
                        return bestBreak.offsetTop * pixelsToMm * scale;
                    }

                    return targetYMm;
                };

                let currentYMm = 0;
                let pageCount = 0;

                while (currentYMm < scaledHeightMm) {
                    if (pageCount > 0) {
                        pdf.addPage();
                    }

                    // Calculate ideal page content height
                    const remainingHeightMm = scaledHeightMm - currentYMm;
                    const idealPageHeightMm = Math.min(contentHeight, remainingHeightMm);
                    const maxYMm = currentYMm + idealPageHeightMm;

                    // Find the best break point
                    const actualPageEndMm = findBestBreakPoint(
                        currentYMm + idealPageHeightMm,
                        maxYMm
                    );
                    const pageContentHeightMm = actualPageEndMm - currentYMm;

                    // Convert to pixels for canvas cropping
                    const startYPx = currentYMm / scale / pixelsToMm;
                    const pageContentHeightPx = pageContentHeightMm / scale / pixelsToMm;

                    // Create page canvas
                    const pageCanvas = document.createElement('canvas');
                    pageCanvas.width = canvas.width;
                    pageCanvas.height = Math.ceil(pageContentHeightPx);

                    const ctx = pageCanvas.getContext('2d');
                    if (ctx) {
                        // White background
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

                        // Draw the content slice
                        ctx.drawImage(
                            canvas,
                            0,
                            startYPx,
                            canvas.width,
                            pageContentHeightPx,
                            0,
                            0,
                            canvas.width,
                            pageContentHeightPx
                        );
                    }

                    // Add to PDF
                    const imgData = pageCanvas.toDataURL('image/jpeg', 0.9);
                    pdf.addImage(
                        imgData,
                        'JPEG',
                        margin,
                        margin,
                        contentWidth,
                        pageContentHeightMm
                    );

                    // Move to the next page
                    currentYMm = actualPageEndMm;
                    pageCount++;

                    // Safety check to prevent infinite loops
                    if (pageCount > 50) {
                        console.warn('PDF generation stopped: too many pages');
                        break;
                    }
                }

                pdf.save('Cardholder Agreement.pdf');
            } catch (error) {
                console.error('Error generating PDF:', error);
            } finally {
                // Cleanup
                element.classList.remove('pdf-content');
                document.head.removeChild(style);

                // Restore original styles
                element.style.height = originalHeight;
                element.style.maxHeight = originalMaxHeight;
                element.style.overflow = originalOverflow;
            }
        }
    };

    const { agreement } = useAgreements({
        agreementId: agreement_document_id,
        isInitialAgreement: true
    });

    const cardholderTable = `
        <h2>Cardholder INFORMATION & AGREEMENT</h2>
        <p>
            <b>Part 1: Cardholder Information</b>
        </p>
        <table>
            <tbody>
                <tr>
                    <td colSpan="3">
                        <b>First Name:</b>
                        <br /> ${agreement_details?.first_name || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Middle Name:</b>
                        <br /> ${agreement_details?.middle_name || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Last Name:</b>
                        <br /> ${agreement_details?.last_name || '-'}
                    </td>
                </tr>
                <tr>
                    <td colSpan="3">
                        <b>Alias:</b>
                        <br /> ${agreement_details?.alias || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Date of Birth:</b>
                        <br />
                        ${formatDate(agreement_details?.birth_date, 'MM/DD/YYYY') || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Occupation:</b>
                        <br /> ${occupation?.[agreement_details?.occupation] || '-'}
                    </td>
                </tr>
                <tr>
                    <td colSpan="3">
                        <b>Cell Phone Number:</b>
                        <br /> ${agreement_details?.mobile_phone || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Phone Number (Home):</b>
                        <br /> ${agreement_details?.home_phone || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Email:</b>
                        <br /> ${agreement_details?.email || '-'}
                    </td>
                </tr>
                <tr>
                    <td colSpan="9">
                        <b>Permanent Residence Address:</b>
                        <br />
                        ${`${agreement_details?.permanent_residence?.address_1},${agreement_details?.permanent_residence?.address_2 ? ` ${agreement_details?.permanent_residence?.address_2},` : ''} ${agreement_details?.permanent_residence?.city}, ${agreement_details?.permanent_residence?.region ? ` ${agreement_details?.permanent_residence?.region},` : ''} ${agreement_details?.permanent_residence?.zip_code}, ${agreement_details?.permanent_residence?.country}`}
                    </td>
                </tr>
                <tr>
                    <td colSpan="3">
                        <b>Address line 1:</b>
                        <br /> ${agreement_details?.permanent_residence?.address_1 || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Address line 2:</b>
                        <br /> ${agreement_details?.permanent_residence?.address_2 || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Country:</b>
                        <br /> ${agreement_details?.permanent_residence?.country || '-'}
                    </td>
                </tr>
                <tr>
                    <td colSpan="3">
                        <b>City:</b>
                        <br /> ${agreement_details?.permanent_residence?.city || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Province:</b>
                        <br /> ${agreement_details?.permanent_residence?.region || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Postal Code:</b>
                        <br /> ${agreement_details?.permanent_residence?.zip_code || '-'}
                    </td>
                </tr>
            </tbody>
        </table>
        <hr />
        <p>
            <b>Part 2: Declaration of Tax Residence</b>
        </p>
        <table>
            <tbody>
            ${agreement_details?.tax_info
                ?.map((item) => {
                    return `
                <tr>
                    <td colSpan="3">
                        <b>Jurisdiction of tax residence:</b>
                        <br /> ${item?.country || '-'}
                    </td>
                    <td colSpan="3">
                        <b>Social Insurance Number:</b>
                        <br /> ${item?.tin || '-'}
                    </td>
                    <td colSpan="3">
                        ${
                            item?.tin
                                ? ''
                                : `
                                <b>Reason you do not have a SIN:</b>
                                <br /> ${item?.reason || '-'}
                            `
                        }
                    </td>
                </tr>
                `;
                })
                .join('')}
            </tbody>
        </table>
        <hr />
        <p>
            <b>Part 3: Certification of Information and Cardholder Agreement</b>
        </p>
        <p>
            You certify that the information given on this form is correct and complete.
            You must undertake to give DCBank a new form within 30 days of any change in
            circumstance that causes the information on this form to become incomplete
            or inaccurate. By signing the below, you acknowledge that you have received
            and read the entire Agreement, accepted its terms and conditions, and agree
            to the fees that are detailed herein.
        </p>
        <table>
            <tbody>
                <tr>
                    <td colSpan="6">
                        <b>Signature:</b>
                        <span className={styles.agreement}>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M14.334 0.75H5.665C2.644 0.75 0.75 2.889 0.75 5.916V14.084C0.75 17.111 2.635 19.25 5.665 19.25H14.333C17.364 19.25 19.25 17.111 19.25 14.084V5.916C19.25 2.889 17.364 0.75 14.334 0.75Z"
                                    stroke="#DB9E2F"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M6.44141 9.99995L8.81541 12.373L13.5614 7.62695"
                                    stroke="#DB9E2F"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <br />
                        You have agreed to the Cardholder Agreement by checking the box:
                        “I have read, understood and agree to the Cardholder Agreement
                        above”
                    </td>
                    <td colSpan="3">
                        <b>Date:</b>
                        <br /> ${formatDate(agreement_accepted_at, 'MM/DD/YYYY') || '-'}
                    </td>
                </tr>
            </tbody>
        </table>
        `;

    return (
        <div className={styles.wrapper}>
            <h1 className={styles.title}>Agreement</h1>
            <div className={styles.content_wrapper}>
                <div
                    className={styles.content}
                    ref={printRef}
                    dangerouslySetInnerHTML={{
                        __html:
                            agreement?.content?.replace('{{cardholder_data}}', cardholderTable) ||
                            '-'
                    }}
                />
                <div className={styles.content}></div>
            </div>
            <div className={styles.actions}>
                <Button variant="outlined-dark" leftIcon="print" onClick={handlePrint}>
                    Print
                </Button>
                <Button variant="primary" leftIcon="download" onClick={handleDownloadPDF}>
                    Download
                </Button>
            </div>
        </div>
    );
};

export default CardAgreementModal;
